import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  Trophy,
  Medal,
  Award,
  Loader2,
  RefreshCw,
  Clock,
  GripVertical,
  ArrowRight,
  Filter,
  ChevronDown
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIPELINE_STAGES = [
  { key: "Prospect", color: "bg-slate-500", lightBg: "bg-slate-50", border: "border-slate-200" },
  { key: "Outreach Active", color: "bg-blue-500", lightBg: "bg-blue-50", border: "border-blue-200" },
  { key: "Feasibility Out", color: "bg-amber-500", lightBg: "bg-amber-50", border: "border-amber-200" },
  { key: "Site Selected", color: "bg-green-500", lightBg: "bg-green-50", border: "border-green-200" },
  { key: "In Activation", color: "bg-cyan-500", lightBg: "bg-cyan-50", border: "border-cyan-200" },
  { key: "Enrolling", color: "bg-purple-500", lightBg: "bg-purple-50", border: "border-purple-200" },
  { key: "Closeout", color: "bg-indigo-500", lightBg: "bg-indigo-50", border: "border-indigo-200" },
  { key: "Not Selected", color: "bg-red-500", lightBg: "bg-red-50", border: "border-red-200" },
  { key: "Reactivation", color: "bg-teal-500", lightBg: "bg-teal-50", border: "border-teal-200" },
];

const TIER_STYLES = {
  Gold: { icon: Trophy, color: "text-yellow-500", bg: "bg-yellow-100", text: "text-yellow-800" },
  Silver: { icon: Medal, color: "text-slate-400", bg: "bg-slate-100", text: "text-slate-700" },
  Bronze: { icon: Award, color: "text-orange-500", bg: "bg-orange-100", text: "text-orange-800" },
  Unranked: { icon: Users, color: "text-slate-400", bg: "bg-slate-50", text: "text-slate-500" },
};

const TierBadge = ({ tier }) => {
  const style = TIER_STYLES[tier] || TIER_STYLES.Unranked;
  const Icon = style.icon;
  return (
    <Badge className={cn("gap-0.5 text-[10px] px-1.5 py-0", style.bg, style.text, "border-0")}>
      <Icon className="h-2.5 w-2.5" />
      {tier}
    </Badge>
  );
};

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    const now = new Date();
    return Math.floor((now - date) / (1000 * 60 * 60 * 24));
  } catch { return null; }
};

export default function Pipeline() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState("all");
  const [taFilter, setTaFilter] = useState("all");
  const [taOptions, setTaOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [draggedLead, setDraggedLead] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [updating, setUpdating] = useState(null);

  const user = JSON.parse(localStorage.getItem("stryde_user") || "{}");

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (tierFilter !== "all") params.append("tier", tierFilter);
      if (taFilter !== "all") params.append("therapeutic_area", taFilter);
      if (searchQuery) params.append("search", searchQuery);
      params.append("limit", "500");
      const response = await axios.get(`${API}/leads?${params.toString()}`);
      setLeads(response.data || []);
    } catch (error) {
      toast.error("Failed to load pipeline data");
    } finally {
      setLoading(false);
    }
  }, [tierFilter, searchQuery, taFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    axios.get(`${API}/dashboard/therapeutic-areas-list`).then(res => {
      setTaOptions(res.data.therapeutic_areas || []);
    }).catch(() => {});
  }, []);

  const getLeadsForStage = (stageKey) => {
    return leads.filter(lead => (lead.pipeline_stage || "Prospect") === stageKey);
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", lead.id);
  };

  const handleDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget(stageKey);
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    setDropTarget(null);

    if (!draggedLead) return;
    const currentStage = draggedLead.pipeline_stage || "Prospect";
    if (currentStage === targetStage) {
      setDraggedLead(null);
      return;
    }

    setUpdating(draggedLead.id);

    // Optimistic update
    setLeads(prev => prev.map(l =>
      l.id === draggedLead.id ? { ...l, pipeline_stage: targetStage } : l
    ));

    try {
      await axios.put(`${API}/leads/${draggedLead.id}/pipeline-stage`, {
        pipeline_stage: targetStage,
        changed_by: user.name || "System"
      });
      toast.success(`${draggedLead.contact || draggedLead.company} moved to ${targetStage}`);
    } catch (error) {
      // Revert on error
      setLeads(prev => prev.map(l =>
        l.id === draggedLead.id ? { ...l, pipeline_stage: currentStage } : l
      ));
      toast.error("Failed to update pipeline stage");
    } finally {
      setDraggedLead(null);
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pipeline-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1C2A25] font-['Outfit'] tracking-tight">Pipeline Board</h1>
          <p className="text-slate-500 text-sm mt-0.5">Drag contacts between stages to track BD lifecycle</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-48 h-9"
            data-testid="pipeline-search"
          />
          <Select value={tierFilter} onValueChange={setTierFilter}>
            <SelectTrigger className="w-32 h-9" data-testid="pipeline-tier-filter">
              <SelectValue placeholder="All Tiers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tiers</SelectItem>
              <SelectItem value="Gold">Gold</SelectItem>
              <SelectItem value="Silver">Silver</SelectItem>
              <SelectItem value="Bronze">Bronze</SelectItem>
              <SelectItem value="Unranked">Unranked</SelectItem>
            </SelectContent>
          </Select>
          <Select value={taFilter} onValueChange={setTaFilter}>
            <SelectTrigger className="w-40 h-9" data-testid="pipeline-ta-filter">
              <SelectValue placeholder="All TAs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All TAs</SelectItem>
              {taOptions.map(ta => (
                <SelectItem key={ta} value={ta}>{ta}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchLeads} data-testid="pipeline-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stage counts summary */}
      <div className="flex gap-2 flex-wrap">
        {PIPELINE_STAGES.map(stage => {
          const count = getLeadsForStage(stage.key).length;
          return (
            <Badge
              key={stage.key}
              variant="outline"
              className={cn("text-xs gap-1", count > 0 && stage.lightBg)}
              data-testid={`stage-count-${stage.key.replace(/\s/g, "-").toLowerCase()}`}
            >
              <span className={cn("h-2 w-2 rounded-full", stage.color)} />
              {stage.key}: {count}
            </Badge>
          );
        })}
      </div>

      {/* Kanban Board */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max">
          {PIPELINE_STAGES.map(stage => {
            const stageLeads = getLeadsForStage(stage.key);
            const isDropping = dropTarget === stage.key;

            return (
              <div
                key={stage.key}
                className={cn(
                  "w-[260px] flex-shrink-0 rounded-xl border-2 transition-colors",
                  isDropping ? `${stage.border} ${stage.lightBg}` : "border-slate-200 bg-slate-50/50"
                )}
                onDragOver={(e) => handleDragOver(e, stage.key)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stage.key)}
                data-testid={`stage-column-${stage.key.replace(/\s/g, "-").toLowerCase()}`}
              >
                {/* Stage Header */}
                <div className="p-3 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={cn("h-3 w-3 rounded-full", stage.color)} />
                      <h3 className="text-sm font-semibold text-slate-800">{stage.key}</h3>
                    </div>
                    <Badge variant="secondary" className="text-xs">{stageLeads.length}</Badge>
                  </div>
                </div>

                {/* Cards */}
                <ScrollArea className="h-[calc(100vh-300px)] p-2">
                  <div className="space-y-2">
                    {stageLeads.length === 0 ? (
                      <div className="text-center py-8 text-slate-400 text-xs">
                        Drop contacts here
                      </div>
                    ) : (
                      stageLeads.map(lead => {
                        const days = daysSince(lead.last_updated);
                        return (
                          <div
                            key={lead.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, lead)}
                            className={cn(
                              "bg-white rounded-lg border border-slate-200 p-3 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md transition-shadow",
                              updating === lead.id && "opacity-60",
                              draggedLead?.id === lead.id && "opacity-40"
                            )}
                            data-testid={`pipeline-card-${lead.id}`}
                          >
                            <div className="flex items-start justify-between gap-1">
                              <Link to={`/leads/${lead.id}`} className="min-w-0 flex-1" onClick={(e) => e.stopPropagation()}>
                                <p className="text-sm font-semibold text-slate-900 truncate hover:text-blue-600 transition-colors">
                                  {lead.company || "Unknown"}
                                </p>
                                <p className="text-xs text-slate-500 truncate">{lead.contact || "N/A"}</p>
                              </Link>
                              <GripVertical className="h-4 w-4 text-slate-300 flex-shrink-0 mt-0.5" />
                            </div>
                            <div className="flex items-center justify-between mt-2 gap-1 flex-wrap">
                              <TierBadge tier={lead.company_tier} />
                              {days !== null && (
                                <span className={cn(
                                  "text-[10px] flex items-center gap-0.5",
                                  days > 60 ? "text-red-500" : days > 30 ? "text-amber-500" : "text-slate-400"
                                )}>
                                  <Clock className="h-2.5 w-2.5" />
                                  {days}d
                                </span>
                              )}
                            </div>
                            {lead.bd_person_owner && (
                              <p className="text-[10px] text-slate-400 mt-1 truncate">
                                {lead.bd_person_owner}
                              </p>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
